import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import type { ClientDashboardUpcomingEducation } from "../../types/client-dashboard.types";
import { RightChevron } from "@/components/icons";
import styles from "./ClientUpcomingTrainingsTable.module.scss";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return isoDate;
  }
}

import { formatDurationFloat } from "@/utils/format-duration";

function formatDuration(minutes: number): string {
  return formatDurationFloat(minutes);
}

export interface ClientUpcomingTrainingsTableProps {
  items: ClientDashboardUpcomingEducation[];
}

export const ClientUpcomingTrainingsTable: React.FC<
  ClientUpcomingTrainingsTableProps
> = ({ items }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const total = items.length;
  const data = useMemo(() => {
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  const columns: TableColumn<ClientDashboardUpcomingEducation>[] = [
    {
      header: t("clientDashboard.upcomingTrainings.colTrainingName"),
      accessor: (row) => row.educationName,
      className: styles.colName,
    },
    {
      header: t("clientDashboard.upcomingTrainings.colFormat"),
      accessor: (row) => row.typeLabel,
    },
    {
      header: t("clientDashboard.upcomingTrainings.colInstructor"),
      accessor: (row) => row.instructorName,
    },
    {
      header: t("clientDashboard.upcomingTrainings.colCapacity"),
      accessor: (row) => row.participantCapacity || "-",
    },
    {
      header: t("clientDashboard.upcomingTrainings.colStartDate"),
      accessor: (row) => formatDate(row.startDate),
    },
    {
      header: t("clientDashboard.upcomingTrainings.colDuration"),
      accessor: (row) => formatDuration(row.durationMinutes),
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          {t("clientDashboard.upcomingTrainings.title")}
        </h3>
        <button
          type="button"
          className={styles.sectionLink}
          onClick={() => navigate("/dashboard/client-education-requests")}
          aria-label={t("clientDashboard.upcomingTrainings.viewAll")}
        >
          <RightChevron width={20} height={20} />
        </button>
      </div>
      <Table<ClientDashboardUpcomingEducation>
        columns={columns}
        data={data}
        getRowId={(row) => row.educationId}
        totalRecords={total}
        currentPage={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={false}
        hasVisibleActions={false}
      />
    </div>
  );
};

export default ClientUpcomingTrainingsTable;
